import { useEffect, useMemo } from 'react';
import { FieldShell } from './FieldShell';
import type { FieldComponentProps } from './types';

const todayISO = (): string => new Date().toISOString().slice(0, 10);

/**
 * `<input type="date">` only accepts `YYYY-MM-DD`. Server / prefill payloads
 * may send dates as ISO datetimes (`1990-05-15T00:00:00.000Z`) or SQL
 * datetimes (`1990-05-15 00:00:00`) — both render blank in the input. Coerce
 * any string whose first 10 characters look like an ISO date down to that
 * date portion; leave anything unrecognised empty so validation can flag it.
 */
const normalizeToISODate = (raw: unknown): string => {
  if (typeof raw !== 'string' || raw === '') return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
  return m ? m[1] : '';
};

export const DateField = ({ field, value, error, onChange }: FieldComponentProps) => {
  const c = field.constraints || {};
  // Per spec, `auto_select_date` defaults to true and pre-fills today when empty.
  const autoSelect = field.auto_select_date !== false;
  useEffect(() => {
    if (autoSelect && (value === '' || value === null || value === undefined)) {
      onChange(todayISO());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const normalized = useMemo(() => normalizeToISODate(value), [value]);

  // If the raw value isn't a canonical YYYY-MM-DD (e.g. server returned an
  // ISO datetime via existing-submission prefill), push the normalised
  // version back into form state so submit and validation see the right
  // shape. Skips when normalisation produced nothing — leaving validation
  // to surface "must be a valid date" for genuinely bad inputs.
  useEffect(() => {
    if (typeof value !== 'string' || value === '') return;
    if (normalized && normalized !== value) {
      onChange(normalized);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, normalized]);

  const min = c.isFuture ? todayISO() : undefined;
  const max = c.isPast ? todayISO() : undefined;

  return (
    <FieldShell field={field} error={error}>
      <input
        id={field.title}
        type="date"
        className="form-input"
        value={normalized}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldShell>
  );
};
